import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Quiz API service
 * 
 * Provides access to quiz sets, questions, attempts, and AI-powered grading.
 */
export const QuizApi = {
  /**
   * Create quiz set (typically called after AI generation)
   * @param {Object} data - Quiz set data
   * @param {number} data.userId - User ID
   * @param {number} [data.endeavorId] - Endeavor ID (optional)
   * @param {string} data.title - Quiz title
   * @param {string} [data.description] - Quiz description
   * @param {string} data.difficultyLevel - Difficulty: "beginner", "intermediate", or "advanced"
   * @param {number[]} [data.documentIds] - Source document IDs
   * @param {Object[]} data.questions - Array of quiz questions
   * @param {string} data.questions[].questionType - Type: "multiple_choice", "true_false", "short_answer"
   * @param {string} data.questions[].questionText - Question text
   * @param {string} data.questions[].correctAnswer - Correct answer
   * @param {string[]} [data.questions[].options] - Answer options (for multiple choice)
   * @param {string} [data.questions[].explanation] - Answer explanation
   * @param {number[]} [data.questions[].sourceChunkIds] - Source chunk IDs
   * @param {number} [data.questions[].position] - Question position
   * @param {number} [data.generationCostUsd] - Generation cost in USD
   * @returns {Promise<Object>} Created quiz set with questions
   */
  createQuizSet: async (data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/quiz/sets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Get quiz sets (simple list)
   * @param {Object} [options] - Filter options
   * @param {number} [options.endeavorId] - Filter by endeavor ID
   * @returns {Promise<Object[]>} List of quiz sets
   */
  getQuizSets: async (options = {}) => {
    const { endeavorId } = options;
    const queryParams = new URLSearchParams();
    
    if (endeavorId) queryParams.set('endeavorId', endeavorId);
    
    const queryString = queryParams.toString();
    const url = `${DATA_SERVICE_BASE_URL}/api/quiz/sets${queryString ? '?' + queryString : ''}`;
    
    return fetchWithAuth(url, {
      method: 'GET',
    });
  },

  /**
   * List and filter quiz sets with pagination
   * @param {Object} [options] - Query options
   * @param {number} [options.page=0] - Page number (0-indexed)
   * @param {number} [options.size=20] - Page size
   * @param {number} [options.endeavorId] - Filter by endeavor ID
   * @param {number} [options.documentId] - Filter by document ID
   * @param {string} [options.title] - Filter by title (partial match)
   * @param {string} [options.difficultyLevel] - Filter by difficulty: "beginner", "intermediate", "advanced"
   * @param {string} [options.createdAfter] - ISO datetime string
   * @param {string} [options.createdBefore] - ISO datetime string
   * @param {string} [options.sortBy="createdAt"] - Sort field
   * @param {string} [options.sortDirection="DESC"] - Sort direction: "ASC" or "DESC"
   * @returns {Promise<Object>} Paginated quiz set results
   */
  listQuizSets: async (options = {}) => {
    const {
      page = 0,
      size = 20,
      endeavorId,
      documentId,
      title,
      difficultyLevel,
      createdAfter,
      createdBefore,
      sortBy = 'createdAt',
      sortDirection = 'DESC',
    } = options;

    const queryParams = new URLSearchParams({
      page: String(page),
      size: String(size),
      sortBy,
      sortDirection,
    });

    if (endeavorId) queryParams.set('endeavorId', endeavorId);
    if (documentId) queryParams.set('documentId', documentId);
    if (title) queryParams.set('title', title);
    if (difficultyLevel) queryParams.set('difficultyLevel', difficultyLevel);
    if (createdAfter) queryParams.set('createdAfter', createdAfter);
    if (createdBefore) queryParams.set('createdBefore', createdBefore);

    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/quiz/sets/list?${queryParams.toString()}`, {
      method: 'GET',
    });
  },

  /**
   * Get single quiz set with all questions
   * @param {number} id - Quiz set ID
   * @returns {Promise<Object>} Quiz set with questions
   */
  getQuizSet: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/quiz/sets/${id}`, {
      method: 'GET',
    });
  },

  /**
   * Start new quiz attempt
   * @param {number} quizSetId - Quiz set ID
   * @returns {Promise<Object>} Created quiz attempt
   */
  startQuizAttempt: async (quizSetId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/quiz/sets/${quizSetId}/attempts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * Submit quiz answers (triggers AI grading for short-answer questions)
   * @param {number} attemptId - Quiz attempt ID
   * @param {Object} data - Submission data
   * @param {Object[]} data.submissions - Array of answer submissions
   * @param {number} data.submissions[].questionId - Question ID
   * @param {string} data.submissions[].userAnswer - User's answer
   * @returns {Promise<Object>} Graded attempt results
   */
  submitQuizAnswers: async (attemptId, data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/quiz/attempts/${attemptId}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Update attempt with grading results (called by AI service)
   * @param {number} attemptId - Quiz attempt ID
   * @param {Object} gradingData - Grading results from AI service
   * @returns {Promise<Object>} Updated quiz attempt
   */
  updateGradingResults: async (attemptId, gradingData) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/quiz/attempts/${attemptId}/grading`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(gradingData),
    });
  },

  /**
   * Get attempt results with grading details
   * @param {number} attemptId - Quiz attempt ID
   * @returns {Promise<Object>} Quiz attempt with graded submissions
   */
  getAttemptResults: async (attemptId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/quiz/attempts/${attemptId}/results`, {
      method: 'GET',
    });
  },

  /**
   * Get latest attempt for a quiz set
   * @param {number} quizSetId - Quiz set ID
   * @returns {Promise<Object>} Most recent quiz attempt with submissions
   */
  getLatestAttempt: async (quizSetId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/quiz/sets/${quizSetId}/latest-attempt`, {
      method: 'GET',
    });
  },

  /**
   * Get all attempts for a quiz set
   * @param {number} quizSetId - Quiz set ID
   * @returns {Promise<Object[]>} List of quiz attempts
   */
  getQuizAttempts: async (quizSetId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/quiz/sets/${quizSetId}/attempts`, {
      method: 'GET',
    });
  },

  /**
   * Update quiz set (e.g., rename title)
   * @param {number} id - Quiz set ID
   * @param {Object} data - Updated data
   * @param {string} [data.title] - New title
   * @param {string} [data.description] - New description
   * @returns {Promise<Object>} Updated quiz set
   */
  updateQuizSet: async (id, data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/quiz/sets/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete quiz set
   * @param {number} id - Quiz set ID
   * @returns {Promise<void>} Delete confirmation
   */
  deleteQuizSet: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/quiz/sets/${id}`, {
      method: 'DELETE',
    });
  },
};
